import type { ReactNode } from 'react';
import { AppLayout, useResizeObserver } from '@ksyos/design-system';
import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import type { IReduxState } from '@frontend/configureStore';
import { useUiStateContext } from '@frontend/utils';
import { useSplitViewContext } from '@frontend/utils/hooks/useSplitViewContext';
import { Navigation } from '../elements/navigation/Navigation';
import { EmploymentTaxesDetailsAlert } from '../pages/employments/EmploymentTaxesDetailsAlert';
import { VersionChecker } from '../VersionChecker';

export interface IMasterTemplateProps {
    children: ReactNode;
    navigation?: boolean;
}

function Banner({ children }: Readonly<{ children: ReactNode }>) {
    const { dispatchUiStateAction } = useUiStateContext();
    const bannerRef = useRef(null);
    const { height = 0 } = useResizeObserver({ ref: bannerRef });

    useEffect(() => {
        dispatchUiStateAction({ bannerHeight: height, type: 'setBannerHeight' });
    }, [height]);

    return <div ref={bannerRef}>{children}</div>;
}

export function MasterTemplate({ children, navigation = true }: IMasterTemplateProps) {
    const { areEmploymentTaxesDetailsRequired } = useSelector((state: IReduxState) => state.authentication);
    const { uiState } = useUiStateContext();
    const { isSplitViewOpen } = useSplitViewContext();

    return (
        <AppLayout
            banner={{ children: renderBanner(), height: uiState.bannerHeight }}
            header={navigation && <Navigation />}
            isSplitViewOpen={isSplitViewOpen}
        >
            {children}
        </AppLayout>
    );

    function renderBanner() {
        return (
            <Banner>
                <VersionChecker />
                {areEmploymentTaxesDetailsRequired && <EmploymentTaxesDetailsAlert />}
            </Banner>
        );
    }
}
